import React, { useState, useEffect, useCallback } from 'react';
import API_URL from './config';

function UserProfile({ token, onClose, onUpdate }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
    organization: '',
    email_reminders_enabled: false,
    sms_reminders_enabled: false
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch profile');
      setProfile(data);
      setForm({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        address_line_2: data.address_line_2 || '',
        city: data.city || '',
        state: data.state || '',
        zip_code: data.zip_code || '',
        organization: data.organization || '',
        email_reminders_enabled: data.email_reminders_enabled || false,
        sms_reminders_enabled: data.sms_reminders_enabled || false
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update profile');
      
      setMessage('Profile updated successfully!');
      setProfile(data);
      setSaved(true);
      if (onUpdate) onUpdate(data);
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="profile-modal-overlay">
        <div className="profile-modal">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-modal-overlay">
      <div className="profile-modal">
        <div className="profile-header">
          <h2>My Profile</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {message && <div className="success">{message}</div>}
        {error && <div className="error">{error}</div>}

        {profile && (
          <div className="profile-info">
            <p><strong>Username:</strong> {profile.username}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-row">
            <label>
              First Name *
              <input
                type="text"
                value={form.first_name}
                onChange={e => setForm({ ...form, first_name: e.target.value })}
                required
              />
            </label>
            <label>
              Last Name *
              <input
                type="text"
                value={form.last_name}
                onChange={e => setForm({ ...form, last_name: e.target.value })}
                required
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Email *
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                required
              />
            </label>
            <label>
              Phone
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Organization
              <input
                type="text"
                value={form.organization}
                onChange={e => setForm({ ...form, organization: e.target.value })}
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Address
              <input
                type="text"
                value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })}
              />
            </label>
          </div>
          <div className="form-row">
            <label>
              Address Line 2
              <input
                type="text"
                value={form.address_line_2}
                onChange={e => setForm({ ...form, address_line_2: e.target.value })}
                placeholder="Suite, Apt, etc."
              />
            </label>
          </div>
          <div className="form-row city-state-zip-row">
            <label className="city-label">
              City
              <input
                type="text"
                value={form.city}
                onChange={e => setForm({ ...form, city: e.target.value })}
              />
            </label>
            <label className="state-label">
              State
              <select
                value={form.state}
                onChange={e => setForm({ ...form, state: e.target.value })}
              >
                <option value="">Select</option>
                <option value="AL">AL</option>
                <option value="AK">AK</option>
                <option value="AZ">AZ</option>
                <option value="AR">AR</option>
                <option value="CA">CA</option>
                <option value="CO">CO</option>
                <option value="CT">CT</option>
                <option value="DE">DE</option>
                <option value="FL">FL</option>
                <option value="GA">GA</option>
                <option value="HI">HI</option>
                <option value="ID">ID</option>
                <option value="IL">IL</option>
                <option value="IN">IN</option>
                <option value="IA">IA</option>
                <option value="KS">KS</option>
                <option value="KY">KY</option>
                <option value="LA">LA</option>
                <option value="ME">ME</option>
                <option value="MD">MD</option>
                <option value="MA">MA</option>
                <option value="MI">MI</option>
                <option value="MN">MN</option>
                <option value="MS">MS</option>
                <option value="MO">MO</option>
                <option value="MT">MT</option>
                <option value="NE">NE</option>
                <option value="NV">NV</option>
                <option value="NH">NH</option>
                <option value="NJ">NJ</option>
                <option value="NM">NM</option>
                <option value="NY">NY</option>
                <option value="NC">NC</option>
                <option value="ND">ND</option>
                <option value="OH">OH</option>
                <option value="OK">OK</option>
                <option value="OR">OR</option>
                <option value="PA">PA</option>
                <option value="RI">RI</option>
                <option value="SC">SC</option>
                <option value="SD">SD</option>
                <option value="TN">TN</option>
                <option value="TX">TX</option>
                <option value="UT">UT</option>
                <option value="VT">VT</option>
                <option value="VA">VA</option>
                <option value="WA">WA</option>
                <option value="WV">WV</option>
                <option value="WI">WI</option>
                <option value="WY">WY</option>
              </select>
            </label>
            <label className="zip-label">
              Zip Code
              <input
                type="text"
                value={form.zip_code}
                onChange={e => setForm({ ...form, zip_code: e.target.value })}
              />
            </label>
          </div>
          
          <div className="form-row checkbox-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.email_reminders_enabled}
                onChange={e => setForm({ ...form, email_reminders_enabled: e.target.checked })}
              />
              <span>Enable email reminders 24 hours before my bookings</span>
            </label>
          </div>
          
          <div className="form-row checkbox-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={form.sms_reminders_enabled}
                onChange={e => setForm({ ...form, sms_reminders_enabled: e.target.checked })}
                disabled={!form.phone}
              />
              <span>Enable SMS text reminders 24 hours before my bookings</span>
              {!form.phone && <small style={{display: 'block', color: '#666', marginTop: '4px'}}>(Requires phone number)</small>}
            </label>
          </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              {saved ? 'Close' : 'Cancel'}
            </button>
            <button type="submit" className="save-btn">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserProfile;
